import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, ArrowRight, RotateCcw, Star, Flame, GraduationCap, MessageCircle, ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { lt } from "../localize";
import { BADGE_DEFS } from "../badges";
import { XpAnimation } from "./XpAnimation";
import { Confetti } from "./Confetti";
import { LessonDiscussion } from "./LessonDiscussion";
import { CompletionCertificate } from "./CompletionCertificate";
import { getProgress } from "../progress-store";
import { getTotalLessonCount } from "../content";
import { useAuthStore } from "@/features/auth/auth-store";
import type { Lesson } from "../types";

interface LessonCompleteProps {
  lesson: Lesson;
  quizCorrect: boolean;
  nextLesson?: Lesson;
  earnedXp?: number;
  newBadges?: string[];
  onNextLesson: () => void;
  onBackToAcademy: () => void;
}

export const LessonComplete = ({
  lesson,
  quizCorrect,
  nextLesson,
  earnedXp = 0,
  newBadges = [],
  onNextLesson,
  onBackToAcademy,
}: LessonCompleteProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.language;
  const [showXp, setShowXp] = useState(earnedXp > 0);
  const [showConfetti, setShowConfetti] = useState(quizCorrect);
  const [showDiscussion, setShowDiscussion] = useState(false);
  const [showCertificate, setShowCertificate] = useState(false);
  const username = useAuthStore((s) => s.user?.username) ?? "Player";

  // Check if all lessons are now completed — trigger certificate once
  useEffect(() => {
    const progress = getProgress();
    const completedCount = Object.values(progress).filter((p) => p.completed).length;
    const totalCount = getTotalLessonCount();
    const alreadyShown = localStorage.getItem("certificate-shown") === "true";

    if (completedCount >= totalCount && !alreadyShown) {
      setShowCertificate(true);
      localStorage.setItem("certificate-shown", "true");
    }
  }, []);

  // Show "Apply Now" for levels 2-6 (moduleId "2" through "6")
  const moduleNum = parseInt(lesson.moduleId, 10);
  const showApplyNow = moduleNum >= 2 && moduleNum <= 6;

  const handleApplyNow = () => {
    localStorage.setItem(
      "coach-guided-practice",
      JSON.stringify({ lessonId: lesson.id, guided: true }),
    );
    navigate(`/play?guided=${lesson.id}`);
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-4 text-center space-y-5">
      {/* Confetti for perfect quiz */}
      {showConfetti && <Confetti onComplete={() => setShowConfetti(false)} />}

      {/* Icon */}
      <div
        className={`rounded-full p-5 ${quizCorrect ? "bg-primary/20" : "bg-destructive/20"}`}
        style={{ animation: "scaleIn 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards" }}
      >
        {quizCorrect ? (
          <CheckCircle2 className="w-14 h-14 text-primary" />
        ) : (
          <XCircle className="w-14 h-14 text-destructive" />
        )}
      </div>

      {/* Title */}
      <div className="space-y-1">
        <h2 className="text-2xl font-bold">{t("learn_complete")}</h2>
        <p className="text-sm text-muted-foreground">{lt(lesson.title, lang)}</p>
      </div>

      {/* XP earned + quiz result */}
      <div className="flex items-center gap-4">
        {earnedXp > 0 && (
          <div className="relative glass px-4 py-2.5 flex items-center gap-2">
            <Star className="w-5 h-5 text-secondary" />
            <span className="font-bold text-secondary">+{earnedXp} XP</span>
            {showXp && <XpAnimation amount={earnedXp} onComplete={() => setShowXp(false)} />}
          </div>
        )}
        <div className="glass px-4 py-2.5 flex items-center gap-2">
          {quizCorrect ? (
            <>
              <CheckCircle2 className="w-4 h-4 text-primary" />
              <span className="font-bold text-primary text-sm">{t("learn_quiz_correct")}</span>
            </>
          ) : (
            <>
              <XCircle className="w-4 h-4 text-destructive" />
              <span className="font-bold text-destructive text-sm">{t("learn_quiz_incorrect")}</span>
            </>
          )}
        </div>
      </div>

      {/* New badges */}
      {newBadges.length > 0 && (
        <div className="glass border-secondary/30 p-4 w-full max-w-xs space-y-2">
          <p className="text-xs font-semibold text-secondary uppercase tracking-wider">
            {lang === "es" ? "Logros desbloqueados" : "Badges Earned"}
          </p>
          <div className="flex items-center justify-center gap-3">
            {newBadges.map((key) => {
              const badge = BADGE_DEFS[key];
              if (!badge) return null;
              return (
                <div key={key} className="text-center">
                  <span className="text-2xl">{badge.icon}</span>
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    {lang === "es" ? badge.nameEs : badge.nameEn}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Review hint */}
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Flame className="w-3.5 h-3.5 text-orange-400" />
        <span>{lang === "es" ? "Repaso programado para mañana" : "Review scheduled for tomorrow"}</span>
      </div>

      {/* Action buttons */}
      <div className="space-y-3 w-full max-w-xs pt-2">
        {nextLesson && (
          <Button
            className="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground"
            onClick={onNextLesson}
          >
            <span className="flex items-center gap-2">
              {t("learn_next_lesson")}
              <ArrowRight className="w-4 h-4" />
            </span>
          </Button>
        )}
        {showApplyNow && (
          <Button
            variant="outline"
            className="w-full border-primary text-primary hover:bg-primary/10"
            onClick={handleApplyNow}
          >
            <span className="flex items-center gap-2">
              <GraduationCap className="w-4 h-4" />
              {t("coach_practice")}
            </span>
          </Button>
        )}
        <Button variant="outline" className="w-full" onClick={onBackToAcademy}>
          <span className="flex items-center gap-2">
            <RotateCcw className="w-4 h-4" />
            {t("learn_back_to_academy")}
          </span>
        </Button>
      </div>

      {/* Collapsible Discussion section */}
      <div className="w-full max-w-md pt-4">
        <button
          onClick={() => setShowDiscussion(!showDiscussion)}
          className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          <MessageCircle className="w-3.5 h-3.5" />
          <span>
            {lang === "es" ? "Ver discusion (3)" : "View discussion (3)"}
          </span>
          <ChevronDown
            className={`w-3.5 h-3.5 transition-transform ${showDiscussion ? "rotate-180" : ""}`}
          />
        </button>
        {showDiscussion && (
          <div className="mt-3">
            <LessonDiscussion lessonId={lesson.id} />
          </div>
        )}
      </div>

      {/* Certificate overlay */}
      {showCertificate && (
        <CompletionCertificate
          username={username}
          completedAt={new Date().toISOString()}
          totalLessons={getTotalLessonCount()}
          onClose={() => setShowCertificate(false)}
        />
      )}
    </div>
  );
};
